// Runtime configuration, fetched from `config.json` at the site root before
// the app first renders (see main.tsx). `deploy.yml` writes it from
// `terraform output` after apply (ADR 0024), so one build serves any
// environment.
//
// Same-origin behind the BFF (ADR 0057, following ADR 0055's pattern) — no
// `apiBase` here, every call is a relative `/api/*` / `/auth/*`. The only
// thing left is the OTel namespace.

export interface RuntimeConfig {
  // Shared OTel resource namespace (ADR 0015), written at deploy from the
  // `otel_namespace` Terraform output, so a browser span carries the same
  // `service.namespace` as every backend service. Absent in local dev — the
  // tracer omits the attribute.
  otelNamespace?: string;

  // The storefront's own origin (#261, ADR 0062) — the impersonate button
  // sends the browser there directly (a cross-origin cookie handoff, so it
  // can't just be an API response). Same multi-URL config.json pattern
  // home-ui already uses (ADR 0024). Absent in local dev, or when the
  // storefront isn't deployed — the button is hidden.
  storefrontOrigin?: string;
}

let loaded: RuntimeConfig = {};

export function setRuntimeConfig(cfg: unknown): void {
  loaded = cfg && typeof cfg === "object" ? (cfg as RuntimeConfig) : {};
}

/** Best-effort fetch of `${BASE_URL}config.json`. Never throws. */
export async function fetchRuntimeConfig(): Promise<void> {
  try {
    const res = await fetch(`${import.meta.env.BASE_URL}config.json`, { cache: "no-store" });
    if (res.ok) setRuntimeConfig(await res.json());
  } catch {
    // no config.json (local dev, or not written yet) — fall through
  }
}

/** Shared OTel resource namespace (ADR 0015), or "" when not configured. */
export function otelNamespace(): string {
  return (loaded.otelNamespace ?? "").trim();
}

/** The storefront's origin (#261), trailing slash trimmed, or "" when not configured. */
export function storefrontOrigin(): string {
  return (loaded.storefrontOrigin ?? "").trim().replace(/\/+$/, "");
}
